import React, { useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next';
import { keyBy, filter } from 'lodash';
import { useEntitiesWithGC } from 'cityads-v3/hooks/entities';
import { ParkedDomainStatusId } from 'cityads-v3/hooks/domainParking';
import { useCheckPermission } from 'cityads-v3/hooks/permissions';
import { ParkedDomain, ParkedDomainList, ParkedDomainStatus } from 'cityads-v3/types/domainParking';
import classes from './ParkedDomains.scss'

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddDomain, { AddDomainProps } from 'cityads-v3/containers/webmaster/tools/domainParking/AddDomain';
import AddIcon from '@mui/icons-material/AddOutlined'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import Tooltip from 'cityads-v3/components/Tooltip/Tooltip'
import Typography from '@mui/material/Typography'

type ParkedDomainsProps = TextFieldProps
  & Partial<Pick<AddDomainProps, 'domainType'>>
  & {
    otherDomains?: { name: string, value: number }[],
    otherValue?: number,
    onChangeOtherDomain?: (evt: React.ChangeEvent<HTMLInputElement>) => void,
    showDefaultDomain?: boolean,
    value: ParkedDomain['id']
  }

const ParkedDomains: React.FC<ParkedDomainsProps> = function ParkedDomains({
  domainType,
  onChange,
  onChangeOtherDomain,
  otherDomains,
  otherValue,
  showDefaultDomain = true,
  value,
  ...rest
}) {
  const { t } = useTranslation(['DomainParking']);
  const checkPermission = useCheckPermission();
  const hasParkingPermission = checkPermission('show_domain_parking');

  const [
    parkedDomains,
    parkedDomainsLoading,
    parkedDomainsLoaded,
    loadParkedDomains
  ] = useEntitiesWithGC<ParkedDomainList>('parkedDomains');

  const statusById = useDomainStatusesById();
  const parkedDomainsById = useMemo(() => keyBy(parkedDomains?.parkedDomain, 'id'), [parkedDomains])
  const otherDomainsById = useMemo(() => keyBy(otherDomains, 'value'), [otherDomains])

  const loadDomainList = useCallback(() => {
    if (hasParkingPermission && !parkedDomainsLoading) {
      loadParkedDomains({
        perpage: 1000,
      })
    }
  }, [hasParkingPermission, parkedDomainsLoading, loadParkedDomains])

  useEffect(() => {
    if (hasParkingPermission && !parkedDomainsLoading && !parkedDomainsLoaded) {
      loadParkedDomains({
        perpage: 1000,
      })
    }
  }, [hasParkingPermission, parkedDomainsLoading, parkedDomainsLoaded, loadParkedDomains])

  const handleChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    if (typeof value === 'undefined') {
      return false;
    }
    const isOther = String(value).indexOf('other-') === 0;

    onChange?.({
      ...evt,
      target: {
        ...evt.target,
        value: isOther ? null : value
      }
    })

    onChangeOtherDomain?.({
      ...evt,
      target: {
        ...evt.target,
        value: isOther ? value.replace('other-', '') : null
      }
    })

  }, [
    onChange,
    onChangeOtherDomain
  ])

  const visibleDomains = useMemo(() => {
    const domains = typeof domainType === 'undefined'
      ? parkedDomains?.parkedDomain
      : filter(parkedDomains?.parkedDomain, { domainType })


    return domains?.filter(domain => domain.status !== ParkedDomainStatusId.Failed)
  }, [
    domainType,
    parkedDomains?.parkedDomain
  ])

  if (!hasParkingPermission) {
    return null;
  }

  return (
    <TextField
      select
      label={t('fields.domain')}
      {...rest}
      onChange={handleChange}
      value={otherValue ? `other-${otherValue}` : value || ''}
      slotProps={{
        select: {
          classes: {
            select: classes.selectedValue
          },
          displayEmpty: true,
          endAdornment: (
            <AddDomain
              Component={AddDomainButton}
              domainType={domainType}
              onSuccess={loadDomainList}
            />
          ),
          renderValue(value: string | number) {
            const isOther = String(value).indexOf('other-') === 0;

            if (!value) {
              return t('selectField.defaultDomain')
            }

            return (
              isOther
                ? otherDomainsById[String(value).replace('other-', '')]?.name
                : parkedDomainsById[value]?.domain
            )
          }
        },
        inputLabel: {
          shrink: true
        }
      }}
    >
      {showDefaultDomain && <MenuItem value="">{t('selectField.defaultDomain')}</MenuItem>}
      {otherDomains?.map(domain => (
        <MenuItem
          key={`other-${domain.value}`}
          value={`other-${domain.value}`}
        >
          <Typography variant="inherit" classes={{ root: classes.menuItemDomain }}>
            {domain.name}
          </Typography>
        </MenuItem>
      ))}
      {visibleDomains?.map(domain => {
        const isWaiting = domain.status !== ParkedDomainStatusId.Successful;

        return (
          <MenuItem
            key={domain.id}
            classes={{ root: classes.menuItem }}
            value={isWaiting ? undefined : domain.id}
            disabled={isWaiting}
          >
            <Typography variant="inherit" classes={{ root: classes.menuItemDomain }}>
              {domain.domain}
            </Typography>
            {isWaiting && (
              <Tooltip
                isLight
                placement="right"
                title={statusById[domain.status]?.name}
              >
                <ListItemIcon
                  classes={{ root: classes.menuItemIcon }}
                >
                  <AccessTimeIcon color="secondary" />
                </ListItemIcon>
              </Tooltip>
            )}
          </MenuItem>
        )
      })}
    </TextField>
  )
}

type AddDomainButtonProps = { onClick: () => void };

function AddDomainButton({ onClick }: AddDomainButtonProps) {
  const { t } = useTranslation(['DomainParking']);

  return (
    <Tooltip
      title={t('addNewDomain')}
    >
      <IconButton
        classes={{ root: classes.addButton }}
        size="small"
        onClick={onClick}
      ><AddIcon /></IconButton>
    </Tooltip>
  );
}

function useDomainStatusesById() {
  const [
    statuses,
    isLoading,
    isLoaded,
    load
  ] = useEntitiesWithGC<ParkedDomainStatus[]>('parkedDomainStatuses');

  const statusById = useMemo(() => keyBy(
    statuses,
    'id'
  ), [statuses])

  useEffect(() => {
    if (!isLoading && !isLoaded) {
      load()
    }
  }, [isLoading, isLoaded, load])

  return statusById;
}

export default ParkedDomains;
